import { makeStyles } from 'tss-react/mui';
import {
  PDF_PAGE_THUMB_CARD_INSET_PX,
  PDF_PAGE_THUMB_IMAGE_MAX_EDGE_PX,
} from './PdfImportPageThumbnail';
import {
  PDF_IMPORT_CANVAS_BACKGROUND,
  PDF_IMPORT_PAGE_MAX_WIDTH_PX,
  PDF_IMPORT_PAGE_STACK_GAP_PX,
} from './pdf-import.constants';

/** Checkbox column width. */
export const THUMB_CHECKBOX_COLUMN_PX = 32;
/** Gap between checkbox and thumbnail card. */
export const THUMB_CHECKBOX_GAP_PX = 4;
/** Card footprint used by step-2 assign grid and step-1 rail width. */
const THUMB_CARD_FOOTPRINT_PX =
  PDF_PAGE_THUMB_CARD_INSET_PX * 2 + PDF_PAGE_THUMB_IMAGE_MAX_EDGE_PX;
/** Rail horizontal padding (matches thumbRail `theme.spacing(1)`). */
const THUMB_RAIL_PADDING_X_PX = 8;
/** Room for the thumb rail’s vertical scrollbar so it never forces overflow-x. */
const THUMB_RAIL_SCROLLBAR_GUTTER_PX = 10;
/** Hug the thumbnail column: padding + checkbox + gap + card + padding + scrollbar. */
const LEFT_NAV_WIDTH_PX =
  THUMB_RAIL_PADDING_X_PX * 2 +
  THUMB_CHECKBOX_COLUMN_PX +
  THUMB_CHECKBOX_GAP_PX +
  THUMB_CARD_FOOTPRINT_PX +
  THUMB_RAIL_SCROLLBAR_GUTTER_PX;
/** Matches `content` horizontal padding (`theme.spacing(3)`). */
const DIALOG_CONTENT_PADDING_X_PX = 24;
/** Room for the continuous viewer’s vertical scrollbar. */
const STEP1_VIEWER_SCROLLBAR_GUTTER_PX = 16;
/**
 * Preview pane hugs the page + side insets equal to the stack gap between pages.
 */
const STEP1_PREVIEW_PANE_WIDTH_PX =
  PDF_IMPORT_PAGE_MAX_WIDTH_PX +
  PDF_IMPORT_PAGE_STACK_GAP_PX * 2 +
  STEP1_VIEWER_SCROLLBAR_GUTTER_PX;
/** Step 1 — thumb rail + viewer (no excess side canvas). */
const STEP1_DIALOG_WIDTH_PX =
  DIALOG_CONTENT_PADDING_X_PX * 2 +
  LEFT_NAV_WIDTH_PX +
  STEP1_PREVIEW_PANE_WIDTH_PX;
const STEP1_DIALOG_WIDTH = `min(${STEP1_DIALOG_WIDTH_PX}px, calc(100vw - 48px))`;
const DIALOG_HEIGHT = 'min(960px, calc(100vh - 48px))';
/** Matches `theme.spacing(1.5)` — gap between page / arrow / storey columns. */
const STEP2_COLUMN_GAP_PX = 12;
/** Horizontal inset around the assign rows inside the step-2 list. */
const STEP2_LIST_PADDING_X_PX = 32;
const STEP2_CONTENT_PADDING_X_PX = 24;
/** Extra width so the step-2 module has a bit of breathing room. */
const STEP2_MODULE_BREATHING_PX = 96;
/** Page thumb column — bitmap only (no selection chrome in step 2). */
const STEP2_PAGE_COL_WIDTH_PX = PDF_PAGE_THUMB_IMAGE_MAX_EDGE_PX;
/** Slot for the → between page thumb and storey card. */
const STEP2_ARROW_SLOT_PX = 32;
/** Storey dropdown box — same width as the page thumb card. */
const STEP2_STOREY_CARD_WIDTH_PX = STEP2_PAGE_COL_WIDTH_PX;
/** One assign row: page thumb | → | storey card. */
const STEP2_ASSIGN_ROW_WIDTH_PX =
  STEP2_PAGE_COL_WIDTH_PX +
  STEP2_COLUMN_GAP_PX +
  STEP2_ARROW_SLOT_PX +
  STEP2_COLUMN_GAP_PX +
  STEP2_STOREY_CARD_WIDTH_PX;
/** Room for the page-list vertical scrollbar so it never steals row width. */
const STEP2_SCROLLBAR_GUTTER_PX = 16;
/** Dialog sized to the mapping rows, plus a little room around them. */
const STEP2_DIALOG_WIDTH_PX =
  STEP2_CONTENT_PADDING_X_PX * 2 +
  STEP2_LIST_PADDING_X_PX * 2 +
  STEP2_ASSIGN_ROW_WIDTH_PX +
  STEP2_SCROLLBAR_GUTTER_PX +
  STEP2_MODULE_BREATHING_PX;
const STEP2_DIALOG_WIDTH = `min(${STEP2_DIALOG_WIDTH_PX}px, calc(100vw - 48px))`;

export const usePdfBlueprintUnderlayDialogStyles = makeStyles()((theme) => ({
  backdrop: {
    backgroundColor: 'rgba(250, 250, 252, 0.72)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    // Outer chrome — Design / MainView surface; shells stay warmSurface.
    backgroundColor: theme.palette.background.default,
    backgroundImage: 'none',
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    overflow: 'hidden',
  },
  // Compact — sized to the continuous PDF viewer + thumb rail.
  paperStep1: {
    width: STEP1_DIALOG_WIDTH,
    minWidth: STEP1_DIALOG_WIDTH,
    maxWidth: STEP1_DIALOG_WIDTH,
    height: DIALOG_HEIGHT,
    minHeight: DIALOG_HEIGHT,
    maxHeight: DIALOG_HEIGHT,
  },
  // Hugs the mapping rows — not the PDF canvas footprint.
  paperStep2: {
    width: STEP2_DIALOG_WIDTH,
    minWidth: STEP2_DIALOG_WIDTH,
    maxWidth: STEP2_DIALOG_WIDTH,
    height: 'auto',
    minHeight: 0,
    maxHeight: DIALOG_HEIGHT,
    overflowX: 'hidden',
  },
  title: {
    flexShrink: 0,
    // Title + subtitle so content shell height matches on every step.
    minHeight: 88,
    padding: theme.spacing(2.5, 3, 1.5),
    color: theme.palette.text.primary,
    boxSizing: 'border-box',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  titleText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  titleHeading: {
    margin: 0,
    fontSize: 22,
    fontWeight: 600,
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
    color: theme.palette.text.primary,
  },
  titleSubtitle: {
    margin: 0,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    overflow: 'hidden',
  },
  contentStep2: {
    flex: '0 0 auto',
    overflow: 'visible',
  },
  step1Layout: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
  },
  step1Shell: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.warmSurface.main,
    boxSizing: 'border-box',
  },
  step1Toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexShrink: 0,
    minHeight: 36,
    // Match thumb-rail checkbox gutter so select-all lines up with page checks.
    paddingLeft: theme.spacing(1),
    // Sit just left of the canvas scrollbar (theme scrollbar ≈ 9px + gutter).
    paddingRight: 18,
    boxSizing: 'border-box',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  step1ToolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
    flex: 1,
  },
  step1Body: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
  },
  bodyShell: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: 'hidden',
    // Same outer frame as step1Shell.
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.warmSurface.main,
    boxSizing: 'border-box',
  },
  // Step 2: same fill as the dialog title / paper — no warmSurface plate.
  step2Shell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: '0 1 auto',
    height: 'fit-content',
    maxHeight: '100%',
    alignSelf: 'stretch',
    border: 'none',
    backgroundColor: theme.palette.background.default,
  },
  step2MainPane: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: '0 1 auto',
    width: '100%',
    height: 'fit-content',
    maxHeight: '100%',
    backgroundColor: theme.palette.background.default,
  },
  leftNav: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    width: LEFT_NAV_WIDTH_PX,
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.warmSurface.main,
    minHeight: 0,
    overflow: 'hidden',
  },
  mainPane: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
    backgroundColor: theme.palette.warmSurface.main,
  },
  fileIcon: {
    width: 16,
    height: 14,
    flexShrink: 0,
    color: theme.palette.text.secondary,
    display: 'block',
  },
  fileName: {
    flex: 1,
    minWidth: 0,
    color: theme.palette.text.primary,
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.35,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mutedText: {
    color: theme.palette.text.secondary,
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    flex: 1,
  },
  thumbRail: {
    display: 'flex',
    flexDirection: 'column',
    // Keep thumbs tight — page gutters for the big viewer live in PdfContinuousPageViewer.
    gap: 0,
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: theme.spacing(0.75, 1, 1.25),
    scrollbarWidth: 'thin',
  },
  thumbItem: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    maxWidth: '100%',
  },
  thumbCheckbox: {
    position: 'relative',
    flexShrink: 0,
    width: THUMB_CHECKBOX_COLUMN_PX,
    padding: 2,
    boxSizing: 'border-box',
    color: theme.palette.grey[500],
    // Suppress MUI’s circular control hover; use a square behind the icon.
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      // Same shape as the tick box (slightly rounded square), a bit larger than the icon.
      width: 24,
      height: 24,
      transform: 'translate(-50%, -50%)',
      borderRadius: 4,
      backgroundColor: 'transparent',
      pointerEvents: 'none',
    },
    '&:hover::before, &.Mui-focusVisible::before': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-checked, &.MuiCheckbox-indeterminate': {
      color: theme.palette.grey[600],
    },
  },
  previewPane: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
      width: 0,
      height: 0,
    },
  },
  pagePosition: {
    flexShrink: 0,
    fontVariantNumeric: 'tabular-nums',
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: 12,
    lineHeight: 1.35,
    whiteSpace: 'nowrap',
  },
  step2List: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: '0 1 auto',
    width: '100%',
    height: 'fit-content',
    maxHeight: '100%',
    minHeight: 0,
    gap: theme.spacing(2),
    // Equal inset so assign rows stay centered with room to breathe.
    padding: theme.spacing(3, 4, 3),
    overflow: 'hidden',
    // Same as module — page/storey cards sit directly on this plate.
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
  },
  buildingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    width: STEP2_ASSIGN_ROW_WIDTH_PX,
    maxWidth: '100%',
  },
  buildingLabel: {
    color: theme.palette.text.secondary,
  },
  pageRows: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 1 auto',
    width: STEP2_ASSIGN_ROW_WIDTH_PX,
    height: 'fit-content',
    maxHeight: 'min(640px, calc(100vh - 280px))',
    minHeight: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    // Step 2 only — generous vertical space between page → storey rows.
    gap: 40,
    // Same as module — no second plate behind the cards.
    backgroundColor: 'transparent',
    scrollbarWidth: 'thin',
  },
  assignRow: {
    display: 'flex',
    alignItems: 'center',
    width: STEP2_ASSIGN_ROW_WIDTH_PX,
    boxSizing: 'border-box',
    backgroundColor: 'transparent',
  },
  assignBody: {
    display: 'grid',
    gridTemplateColumns: `${STEP2_PAGE_COL_WIDTH_PX}px ${STEP2_ARROW_SLOT_PX}px ${STEP2_STOREY_CARD_WIDTH_PX}px`,
    // Top-align so → / storey card sit on the image card band (not the label).
    alignItems: 'start',
    columnGap: STEP2_COLUMN_GAP_PX,
    width: STEP2_ASSIGN_ROW_WIDTH_PX,
    backgroundColor: 'transparent',
  },
  assignArrowSlot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: STEP2_ARROW_SLOT_PX,
    // Height set per row to the page card — centers → on the bitmap.
  },
  assignArrow: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body1.fontSize,
    lineHeight: 1,
    userSelect: 'none',
  },
  // Centers a normal-height dropdown on the page-card band.
  assignShowOnSlot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'stretch',
    width: STEP2_STOREY_CARD_WIDTH_PX,
  },
  assignShowOnCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    padding: theme.spacing(0.25, 0.5),
    borderRadius: 6,
    backgroundColor: PDF_IMPORT_CANVAS_BACKGROUND,
    boxSizing: 'border-box',
    transition: 'background-color 0.12s ease',
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    // Two explicit groups: leave/back left, forward right. MUI's sibling
    // margin rule beats a `margin-left: auto`, so space-between does the work.
    justifyContent: 'space-between',
    flexShrink: 0,
    flexWrap: 'wrap',
    margin: 0,
    // Fixed footer band so content shell height stays even across steps.
    minHeight: 64,
    padding: theme.spacing(1.5, 3, 2),
    gap: theme.spacing(2),
    boxSizing: 'border-box',
    // Match step 1 — no separator above the ghost-button footer.
    borderTop: 'none',
  },
  selectionCount: {
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  actionsStart: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  actionsEnd: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
}));
